import { AdminApi, AdminInvestor } from 'api';
import useApiCall from 'hooks/use-api-call';
import useGoTo from 'hooks/use-go-to';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentUserSelector } from 'core/auth/hooks';
import useTranslate from 'ui/hooks/use-translate';

export const useDeleteAccount = (investor: AdminInvestor, onModalClose: () => void) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [requestFutureDeletion, setRequestFutureDeletion] = useState(false);
  const [deletionSuccess, setDeletionSuccess] = useState(false);
  const [error, setError] = useState(false);

  const translate = useTranslate();
  const { makeAuthenticatedApi } = useApiCall();
  const { currentUser } = useCurrentUserSelector();
  const goToInvestors = useGoTo({ path: '/dashboard/investors' });

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  useEffect(() => {
    if (moment().diff(investor.earliestDeletion, 'days') >= 0) {
      setRequestFutureDeletion(false);
    } else {
      setRequestFutureDeletion(true);
    }
  }, [investor.earliestDeletion]);

  const getModalHeader = useCallback(() => {
    return requestFutureDeletion || !deletionSuccess
      ? `${translate('dashboardIssuerInvestorDetails.deleteAccount.delete')}`
      : `${translate('dashboardIssuerInvestorDetails.deleteAccount.deleted')}`;
  }, [deletionSuccess, requestFutureDeletion]);

  const onConfirm = useCallback(async () => {
    try {
      if (!requestFutureDeletion) {
        await adminApi.adminInvestorsDestroy({ id: investor.id });
      } else {
        await adminApi.adminInvestorsPendingDeletionCreate({
          id: investor.id,
          adminPendingInvestorDeletionRequest: {
            investor: investor.id,
            requestingUser: {
              email: currentUser?.email || '',
            },
            earliestDeletionDatetime: investor.earliestDeletion,
          },
        });
      }
      setDeletionSuccess(true);
    } catch (e) {
      setError(true);
    }
  }, [currentUser?.email, investor?.id, investor?.earliestDeletion, requestFutureDeletion, adminApi]);

  const onBack = () => goToInvestors();
  const onClose = () => onModalClose();

  const canDeleteInvestor = useCallback(() => {
    return investor.activeInvestmentsNo === 0 && investor.wallets.length === 0;
  }, [investor.activeInvestmentsNo, investor.wallets.length]);

  return {
    error,
    deletionSuccess,
    requestFutureDeletion,
    isConfirmed,
    setIsConfirmed,
    canDeleteInvestor,
    getModalHeader,
    onConfirm,
    onBack,
    onClose,
  };
};
